import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Edit2, Trash2, Search, MapPin, Eye, QrCode, Move, Printer, Tag } from 'lucide-react';
import { toast } from 'sonner';
import PrintLabelDialog from '../components/PrintLabelDialog';
import LocationSignDialog from '../components/LocationSignDialog';

const FRONTEND_URL = window.location.origin;

const LocationsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Print + Quick Move dialogs
  const [printPayload, setPrintPayload] = useState(null);
  const [quickMoveSource, setQuickMoveSource] = useState(null);
  const [quickMoveTarget, setQuickMoveTarget] = useState('');
  const [quickMoveBusy, setQuickMoveBusy] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const [formData, setFormData] = useState({
    location_name: '',
    bin_id: '',
    bin_type: 'Storage',
    placement: '',
    parent_location_id: '',
    active_status: true,
  });

  const fetchData = async () => {
    try {
      const response = await api().get('/locations');
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, parent_location_id: formData.parent_location_id || null };
      if (editingItem) {
        await api().put(`/locations/${editingItem.id}`, payload);
        toast.success('Location updated');
      } else {
        await api().post('/locations', payload);
        toast.success('Location created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      location_name: item.location_name,
      bin_id: item.bin_id,
      bin_type: item.bin_type,
      placement: item.placement || '',
      parent_location_id: item.parent_location_id || '',
      active_status: item.active_status,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      await api().delete(`/locations/${id}`);
      toast.success('Location deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      location_name: '',
      bin_id: '',
      bin_type: 'Storage',
      placement: '',
      parent_location_id: '',
      active_status: true,
    });
  };

  const filteredItems = locations.filter(item =>
    item.location_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.bin_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.placement || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBinTypeColor = (type) => {
    switch (type) {
      case 'Receiving': return 'status-inprogress';
      case 'Storage': return 'status-draft';
      case 'WIP': return 'status-released';
      case 'FinishedGoods': return 'status-completed';
      default: return 'status-draft';
    }
  };

  // Download a PNG QR linking to the location-view page
  const handleDownloadQr = async (loc) => {
    try {
      const res = await api().get(`/locations/${loc.id}/qr`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `loc_${loc.bin_id || loc.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download QR');
    }
  };

  // Open the global PrintLabelDialog with a Location-shaped payload
  const handlePrintLocation = (loc) => {
    setPrintPayload({
      id: loc.id,
      item_sku: loc.bin_id,
      item_name: loc.location_name,
      vendor_sku: loc.placement || '',
      barcode_or_qr_value: `${FRONTEND_URL}/locations/view/${loc.id}`,
    });
  };

  const handleQuickMove = async () => {
    if (!quickMoveSource || !quickMoveTarget) return;
    setQuickMoveBusy(true);
    try {
      const { data } = await api().post(`/locations/${quickMoveSource.id}/quick-move`, {
        target_location_id: quickMoveTarget,
      });
      const dst = locations.find(l => l.id === quickMoveTarget);
      if (data.moved === 0) {
        toast(`No items at ${quickMoveSource.location_name} / ${quickMoveSource.bin_id} to move.`);
      } else {
        toast.success(`Moved ${data.moved} item(s) → ${dst ? `${dst.location_name} / ${dst.bin_id}` : 'target'}`);
      }
      setQuickMoveSource(null);
      setQuickMoveTarget('');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Quick Move failed');
    } finally {
      setQuickMoveBusy(false);
    }
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="locations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Locations & Bins</h1>
          <p className="text-slate-500 text-sm">Manage physical storage locations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/locations/map')} className="rounded-sm" data-testid="open-map-btn">
            <MapPin className="w-4 h-4 mr-1" /> Location Map
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Location' : 'Add Location'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Location Name *</Label>
                <Input
                  value={formData.location_name}
                  onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                  placeholder="e.g., Warehouse A"
                  required
                  className="rounded-sm"
                  data-testid="location-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Bin / Shelf ID *</Label>
                <Input
                  value={formData.bin_id}
                  onChange={(e) => setFormData({ ...formData, bin_id: e.target.value })}
                  placeholder="e.g., A-01-01 or Shelf-3"
                  required
                  className="rounded-sm mono"
                  data-testid="location-bin-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Placement <span className="text-xs text-slate-400">(physical hint, e.g., "North Wall")</span></Label>
                <Input
                  value={formData.placement}
                  onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                  placeholder="North Wall, Aisle 3, Receiving Dock…"
                  className="rounded-sm"
                  data-testid="location-placement-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Bin Type *</Label>
                <Select value={formData.bin_type} onValueChange={(v) => setFormData({ ...formData, bin_type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="location-bin-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Receiving">Receiving</SelectItem>
                    <SelectItem value="Storage">Storage</SelectItem>
                    <SelectItem value="WIP">Work In Progress</SelectItem>
                    <SelectItem value="FinishedGoods">Finished Goods</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Parent Location <span className="text-xs text-slate-400">(make this a sub-location · supports multi-level nesting)</span></Label>
                <Select
                  value={formData.parent_location_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, parent_location_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="location-parent-select">
                    <SelectValue placeholder="None — top-level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None — top-level</SelectItem>
                    {(() => {
                      // iter36: collect descendant ids of the editing item to prevent cycles
                      const descendants = new Set();
                      if (editingItem?.id) {
                        const stack = [editingItem.id];
                        while (stack.length) {
                          const cur = stack.pop();
                          locations.forEach(l => {
                            if (l.parent_location_id === cur && !descendants.has(l.id)) {
                              descendants.add(l.id);
                              stack.push(l.id);
                            }
                          });
                        }
                      }
                      // build a depth-prefixed display for clarity
                      const depthOf = (l) => {
                        let d = 0; let cur = l.parent_location_id;
                        const safety = new Set();
                        while (cur && d < 20 && !safety.has(cur)) { safety.add(cur); const p = locations.find(x => x.id === cur); if (!p) break; cur = p.parent_location_id; d += 1; }
                        return d;
                      };
                      return locations
                        .filter(l => l.id !== editingItem?.id && !descendants.has(l.id))
                        .sort((a, b) => (a.location_name || '').localeCompare(b.location_name || ''))
                        .map(l => {
                          const d = depthOf(l);
                          const indent = d ? '— '.repeat(d) : '';
                          return (
                            <SelectItem key={l.id} value={l.id} data-testid={`location-parent-option-${l.id}`}>
                              {indent}{l.location_name} / {l.bin_id}
                            </SelectItem>
                          );
                        });
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label>Active Status</Label>
                <Switch
                  checked={formData.active_status}
                  onCheckedChange={(checked) => setFormData({ ...formData, active_status: checked })}
                  data-testid="location-active-switch"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="location-submit-btn">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-sm"
              data-testid="location-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner" />
            </div>
          ) : filteredItems.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Location Name</TableHead>
                  <TableHead>Bin / Shelf ID</TableHead>
                  <TableHead>Placement</TableHead>
                  <TableHead>Bin Type</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id} data-testid={`location-row-${item.id}`}>
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        onClick={() => navigate(`/locations/view/${item.id}`)}
                        className="text-slate-800 hover:text-orange-600 hover:underline text-left"
                        data-testid={`location-name-link-${item.id}`}
                      >
                        {item.location_name}
                      </button>
                    </TableCell>
                    <TableCell className="mono">{item.bin_id}</TableCell>
                    <TableCell className="text-sm text-slate-600" data-testid={`location-placement-${item.id}`}>
                      {item.placement || <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge ${getBinTypeColor(item.bin_type)}`}>
                        {item.bin_type}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500" data-testid={`location-parent-${item.id}`}>
                      {item.parent_location_id
                        ? (() => {
                            const p = locations.find(l => l.id === item.parent_location_id);
                            return p ? `${p.location_name} / ${p.bin_id}` : '—';
                          })()
                        : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge ${item.active_status ? 'status-released' : 'status-obsolete'}`}>
                        {item.active_status ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => navigate(`/locations/view/${item.id}`)}
                          title="View items at this location"
                          data-testid={`view-location-${item.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleDownloadQr(item)}
                          title="Download location QR"
                          data-testid={`download-qr-location-${item.id}`}
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handlePrintLocation(item)}
                          title="Print location label"
                          data-testid={`print-location-${item.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setSignTarget(item)}
                          title="Print physical sign (pick size, QR linked)"
                          data-testid={`sign-location-${item.id}`}
                        >
                          <Tag className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => { setQuickMoveSource(item); setQuickMoveTarget(''); }}
                          title="Quick Move — relocate items to another location"
                          data-testid={`quick-move-location-${item.id}`}
                        >
                          <Move className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-location-${item.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-location-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <MapPin className="w-12 h-12 mb-3 text-slate-300" />
              <p>No locations found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Move dialog */}
      <Dialog
        open={!!quickMoveSource}
        onOpenChange={(open) => { if (!open) { setQuickMoveSource(null); setQuickMoveTarget(''); } }}
      >
        <DialogContent className="sm:max-w-md" data-testid="quick-move-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="w-5 h-5 text-orange-600" />
              Quick Move — {quickMoveSource?.location_name} / {quickMoveSource?.bin_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Moves <span className="font-medium">all items</span> currently at this location to the target,
              writing a Transfer transaction per item so the move is auditable.
            </p>
            <div className="space-y-2">
              <Label>Target Location *</Label>
              <Select value={quickMoveTarget} onValueChange={setQuickMoveTarget}>
                <SelectTrigger className="rounded-sm" data-testid="quick-move-target-select">
                  <SelectValue placeholder="Choose destination" />
                </SelectTrigger>
                <SelectContent>
                  {locations
                    .filter(l => l.id !== quickMoveSource?.id && l.active_status !== false)
                    .map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.location_name} / {l.bin_id}
                        {l.placement ? ` · ${l.placement}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickMoveSource(null)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handleQuickMove}
              disabled={!quickMoveTarget || quickMoveBusy}
              className="bg-orange-600 hover:bg-orange-700 rounded-sm"
              data-testid="quick-move-confirm-btn"
            >
              <Move className="w-4 h-4 mr-2" /> {quickMoveBusy ? 'Moving…' : 'Move items'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrintLabelDialog
        open={!!printPayload}
        onOpenChange={(open) => { if (!open) setPrintPayload(null); }}
        item={printPayload}
        labelType="Location"
      />

      <LocationSignDialog
        open={!!signTarget}
        onOpenChange={(v) => { if (!v) setSignTarget(null); }}
        location={signTarget}
      />
    </div>
  );
};

export default LocationsPage;
